import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Custom hook for managing search parameters in React (without Next.js).
 */
export const useSearchParams = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);

    const setSearchParams = (
        params: Record<string, string | string[]>,
        rememberQuery = true
    ) => {
        const updatedQuery = new URLSearchParams(rememberQuery ? location.search : '');

        Object.keys(params).forEach((key) => {
            const value = params[key];
            if (Array.isArray(value)) {
                value.forEach((item) => updatedQuery.append(key, item));
            } else {
                updatedQuery.set(key, value);
            }
        });

        navigate({ search: updatedQuery.toString() }, { replace: true });
    };

    const clearSearchParams = (paramsToRemove: string[] = []) => {
        const updatedQuery = new URLSearchParams(location.search);

        if (paramsToRemove.length > 0) {
            paramsToRemove.forEach((param) => updatedQuery.delete(param));
        } else {
            updatedQuery.forEach((_, key) => updatedQuery.delete(key));
        }

        navigate({ search: updatedQuery.toString() }, { replace: true });
    };

    const removeQueryParameters = (url: string, paramsToRemove: string[]): string => {
        const urlObject = new URL(url);

        paramsToRemove.forEach((param) => {
            urlObject.searchParams.delete(param);
        });

        return urlObject.toString();
    };

    const clearSearchParamsExcept = (
        paramsToKeep: string[],
        paramsToSet: Record<string, string | string[]> = {}
    ) => {
        const updatedQuery = new URLSearchParams();

        paramsToKeep.forEach((param) => {
            const value = query.getAll(param);
            value.forEach((item) => updatedQuery.append(param, item));
        });

        Object.keys(paramsToSet).forEach((key) => {
            const value = paramsToSet[key];
            if (Array.isArray(value)) {
                value.forEach((item) => updatedQuery.append(key, item));
            } else {
                updatedQuery.set(key, value);
            }
        });

        navigate({ search: updatedQuery.toString() }, { replace: true });
    };

    return {
        setSearchParams,
        clearSearchParams,
        removeQueryParameters,
        clearSearchParamsExcept
    };
};